import { motion } from "framer-motion";
import {
  Brain,
  Rocket,
  Bell,
  ChartLineUp,
  Wallet,
  Star,
} from "@phosphor-icons/react";
import { SectionHeader } from "@/components/site/SectionHeader";
import { fadeUp, staggerParent, viewport } from "@/lib/motion";

const features = [
  {
    icon: Brain,
    title: "AI Crypto Analysis",
    desc: "Velora Intelligence analyzes multiple market and technical factors to produce an AI Score, Risk Score, a clear recommendation and time-based outlooks.",
    span: "md:col-span-7",
    tall: true,
  },
  {
    icon: Rocket,
    title: "Presale Intelligence",
    desc: "Analyze crypto presales using AI-powered intelligence and external market information where available.",
    span: "md:col-span-5",
  },
  {
    icon: Bell,
    title: "AI Smart Monitoring",
    desc: "Monitor selected crypto assets and receive intelligent notifications and monitoring insights.",
    span: "md:col-span-5",
  },
  {
    icon: ChartLineUp,
    title: "Paper Trading",
    desc: "Test strategies using a virtual portfolio — practice and learn without risking real funds.",
    span: "md:col-span-7",
    tall: true,
  },
  {
    icon: Wallet,
    title: "Portfolio",
    desc: "Track your crypto holdings and monitor portfolio information in one clear view.",
    span: "md:col-span-6",
  },
  {
    icon: Star,
    title: "Watchlist",
    desc: "Follow selected assets and keep the opportunities that matter to you in one place.",
    span: "md:col-span-6",
  },
];

const Card = ({ f }) => (
  <motion.div
    variants={fadeUp}
    className={`card-hover-glow group relative overflow-hidden rounded-3xl border border-white/8 bg-white/[0.02] p-7 ${f.span} ${
      f.tall ? "md:row-span-2 md:p-9" : ""
    }`}
    data-testid={`feature-card-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
  >
    <div
      className="pointer-events-none absolute -right-16 -top-16 h-40 w-40 rounded-full bg-teal-500/10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100"
      aria-hidden="true"
    />
    <div className="relative">
      <span className="grid h-12 w-12 place-items-center rounded-2xl border border-teal-500/25 bg-teal-500/10 text-teal-400">
        <f.icon size={24} weight="duotone" />
      </span>
      <h3 className="mt-6 font-display text-xl font-bold text-white md:text-2xl">
        {f.title}
      </h3>
      <p className="mt-3 text-sm leading-relaxed text-white/55 md:text-base">
        {f.desc}
      </p>
      {f.tall && (
        <div className="mt-8 space-y-2.5">
          {["AI Score", "Risk Score", "Recommendation"].map((k, i) => (
            <div key={k} className="flex items-center justify-between rounded-xl border border-white/6 bg-white/[0.02] px-4 py-3">
              <span className="text-xs text-white/50">{k}</span>
              <div className="h-1.5 w-24 overflow-hidden rounded-full bg-white/8">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-teal-600 to-teal-400"
                  style={{ width: ["78%", "40%", "88%"][i] }}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </motion.div>
);

export const Features = () => (
  <section
    id="features"
    className="relative z-10 mx-auto max-w-6xl scroll-mt-24 px-4 py-24 sm:px-6 md:py-32"
    data-testid="features-section"
  >
    <SectionHeader
      label="Capabilities"
      title="One platform for crypto intelligence"
      desc="Every tool is built to give you clarity — analytical intelligence to inform your own decisions, never guarantees."
    />
    <motion.div
      variants={staggerParent}
      initial="hidden"
      whileInView="show"
      viewport={viewport}
      className="mt-14 grid grid-cols-1 gap-4 md:grid-cols-12"
    >
      {features.map((f) => (
        <Card key={f.title} f={f} />
      ))}
    </motion.div>
  </section>
);
